/**
 * TeamLeaderMobileStats Component
 * 
 * Displays attendance statistics for team leaders with two views:
 * 1. Personal Stats - Shows the leader's own attendance across all events
 * 2. Team Stats - Shows aggregate statistics for all members in the team (including Team Leader)
 * 
 * Features:
 * - Dual tab interface for personal vs team stats
 * - Overall attendance rate card (blue for personal, purple for team)
 * - Three independent filters: Group, Department, Period
 * - Monthly attendance breakdown with rates
 * - Event list with attendance details
 * - Bottom sheet to view individual member attendance for specific events
 * - Pull-to-refresh functionality
 * 
 * Key Calculations:
 * - Overall Rate: Total attendances by current members (INCLUDING Team Leader) / Total PAST events only
 * - Team Stats: Uses only PAST events in selected month/period
 * - Confirmation Rate: Total confirmations / (Total possible = members × past events in month)
 * - Attended Rate: Total attendances / Total possible (past events only)
 * - Missed Rate: Total missed / Total possible (past events only)
 * - Declined Rate: Total declined / Total possible (past events only)
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../../lib/supabase'
import LoadingSpinner from '../../../components/LoadingSpinner'
import TeamLeaderBottomNav from '../../../components/TeamLeaderBottomNav'
import PullToRefresh from '../../../components/PullToRefresh'
import { 
  Bell,
  CalendarBlank,
  CalendarDots,
  CaretDown,
  Info,
  X,
} from '@phosphor-icons/react'

export default function TeamLeaderMobileStats() {
  const navigate = useNavigate()
  const [userData, setUserData] = useState(null)
  const [teamData, setTeamData] = useState(null) // Team data (depth 1)
  const [groups, setGroups] = useState([]) // List of groups under the team (depth 2)
  const [subgroups, setSubgroups] = useState([]) // List of departments (depth 3)
  const [selectedGroup, setSelectedGroup] = useState('all') // Group filter
  const [selectedDepartment, setSelectedDepartment] = useState('all') // Department filter
  const [activeTab, setActiveTab] = useState('personal') // 'personal' or 'team'
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth())
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
  const [activeTooltip, setActiveTooltip] = useState(null) // Track which tooltip is open
  const [monthStats, setMonthStats] = useState({
    confirmed: 0,
    attended: 0,
    missed: 0,
    declined: 0,
    rate: 0,
    total: 0
  })
  const [teamStats, setTeamStats] = useState({
    memberCount: 0,
    totalEvents: 0,
    confirmationRate: 0,
    attendedRate: 0,
    missedRate: 0,
    declinedRate: 0
  })
  const [overallRate, setOverallRate] = useState(0)
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [showBottomSheet, setShowBottomSheet] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [eventMemberDetails, setEventMemberDetails] = useState([])
  const [loadingMembers, setLoadingMembers] = useState(false)
  const [isClosing, setIsClosing] = useState(false)

  useEffect(() => {
    loadStats()
  }, [selectedMonth, selectedYear, activeTab, selectedGroup, selectedDepartment])

  useEffect(() => {
    // Close tooltip when clicking outside
    const handleClickOutside = () => setActiveTooltip(null)
    if (activeTooltip) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [activeTooltip])

  const loadStats = async () => {
    try {
      // ===== AUTHENTICATION & USER DATA =====
      const { data: { user } } = await supabase.auth.getUser()
      
      const { data: userDataResult } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single()
      
      setUserData(userDataResult)

      // ===== TEAM INFORMATION =====
      // Get Team Leader's team (depth 1)
      const { data: teamMembership } = await supabase
        .from('group_members')
        .select('group_id, groups:group_id(id, name, depth)')
        .eq('user_id', user.id)
        .eq('groups.depth', 1)
        .maybeSingle()

      const teamId = teamMembership?.groups?.id
      setTeamData(teamMembership?.groups)

      // Get all groups (depth 2) under this team
      let groupsData = []
      let subgroupsData = []
      if (teamId) {
        const { data: fetchedGroups } = await supabase
          .from('groups')
          .select('id, name')
          .eq('parent_id', teamId)
          .eq('depth', 2)
          .order('name')

        groupsData = fetchedGroups || []
        setGroups(groupsData)

        // Get all subgroups (depth 3) under these groups
        const groupIds = groupsData.map(g => g.id)
        if (groupIds.length > 0) {
          const { data: fetchedSubgroups } = await supabase
            .from('groups')
            .select('id, name, parent_id')
            .in('parent_id', groupIds)
            .eq('depth', 3)
            .order('name')

          subgroupsData = fetchedSubgroups || []
        }
        setSubgroups(subgroupsData)
      }

      const now = new Date()
      
      // ===== DATE RANGE CALCULATION =====
      const monthStart = new Date(selectedYear, selectedMonth, 1)
      const monthEnd = new Date(selectedYear, selectedMonth + 1, 0)
      
      const monthStartStr = monthStart.toISOString().split('T')[0]
      const monthEndStr = monthEnd.toISOString().split('T')[0]

      // ===== FETCH EVENTS =====
      // Get past events only for selected month
      const today = new Date().toISOString().split('T')[0]
      const { data: monthEvents } = await supabase
        .from('events')
        .select('*')
        .eq('org_id', userDataResult.org_id)
        .gte('start_date', monthStartStr)
        .lte('start_date', monthEndStr)
        .lte('start_date', today)  // Only past events
        .order('start_date', { ascending: false })

      // Get PAST events only for overall rate calculation
      const { data: allEvents } = await supabase
        .from('events')
        .select('id, start_date, end_time')
        .eq('org_id', userDataResult.org_id)
        .lte('start_date', today)

      if (activeTab === 'personal') {
        // ===== PERSONAL STATS PROCESSING =====
        // Get user's confirmations
        const { data: allConfirmations } = await supabase
          .from('event_confirmations')
          .select('*')
          .eq('user_id', user.id)

        // Get user's attendance records
        const { data: allAttendance } = await supabase
          .from('attendance_records')
          .select('*')
          .eq('user_id', user.id)

        // Calculate overall attendance rate using PAST events only
        const totalAllEvents = allEvents?.length || 0
        const totalAttended = allAttendance?.length || 0
        const overallAttendanceRate = totalAllEvents > 0 ? Math.round((totalAttended / totalAllEvents) * 100) : 0
        setOverallRate(overallAttendanceRate)

        // Process month events with status
        let confirmedCount = 0
        let attendedCount = 0
        let missedCount = 0
        let declinedCount = 0

        const now = new Date()
        const eventsWithStatus = await Promise.all(
          (monthEvents || []).map(async (event) => {
            const eventEnd = new Date(`${event.start_date}T${event.end_time}`)
            const isPast = eventEnd < now

            // Check attendance
            const attended = allAttendance?.find(a => a.event_id === event.id)
            
            if (attended) {
              attendedCount++
              return { ...event, status: 'attended' }
            }

            // Check confirmation
            const confirmation = allConfirmations?.find(c => c.event_id === event.id)
            
            if (isPast) {
              if (confirmation?.status === 'declined') {
                declinedCount++
                return { ...event, status: 'declined' }
              }
              missedCount++
              return { ...event, status: 'missed' }
            }

            if (confirmation) {
              if (confirmation.status === 'confirmed') {
                confirmedCount++
                return { ...event, status: 'confirmed' }
              }
              if (confirmation.status === 'declined') {
                declinedCount++
                return { ...event, status: 'declined' }
              }
            }

            return { ...event, status: 'no_response' }
          })
        )

        const totalMonthEvents = monthEvents?.length || 0
        const monthRate = totalMonthEvents > 0 ? Math.round((attendedCount / totalMonthEvents) * 100) : 0

        setMonthStats({
          confirmed: confirmedCount,
          attended: attendedCount,
          missed: missedCount,
          declined: declinedCount,
          rate: monthRate,
          total: totalMonthEvents
        })

        setEvents(eventsWithStatus)

      } else {
        // ===== TEAM STATS PROCESSING =====
        
        // Early exit if no team
        if (!teamId) {
          setOverallRate(0)
          setMonthStats({
            confirmed: 0,
            attended: 0,
            missed: 0,
            declined: 0,
            rate: 0,
            total: 0
          })
          setTeamStats({
            memberCount: 0,
            totalEvents: 0,
            confirmationRate: 0,
            attendedRate: 0,
            missedRate: 0,
            declinedRate: 0
          })
          setEvents([])
          return
        }

        // ===== APPLY FILTERS WITH MEMBERSHIP RULES =====
        // Build list of group IDs to query based on filters
        let targetGroupIds = []
        let targetSubgroupIds = []

        // Filter by Group
        let filteredGroups = groupsData
        if (selectedGroup !== 'all') {
          filteredGroups = groupsData.filter(g => g.id === selectedGroup)
        }

        const filteredGroupIds = filteredGroups.map(g => g.id)
        
        // Filter by Department
        let filteredSubgroups = subgroupsData
        if (selectedDepartment !== 'all') {
          // Specific department selected
          filteredSubgroups = subgroupsData.filter(sg => sg.id === selectedDepartment)
        } else if (selectedGroup !== 'all') {
          // Group selected, Department = All → show only subgroups under that group
          filteredSubgroups = subgroupsData.filter(sg => filteredGroupIds.includes(sg.parent_id))
        }

        // MEMBERSHIP RULE: Only include Group Leaders (depth 2) when Department = "All"
        // When specific department selected, exclude Group Leaders
        if (selectedDepartment === 'all') {
          targetGroupIds = filteredGroupIds  // Include Group Leaders
        } else {
          targetGroupIds = []  // Exclude Group Leaders when specific dept selected
        }

        targetSubgroupIds = filteredSubgroups.map(sg => sg.id)

        // Combine depth 2 (groups - only if dept=all) and depth 3 (subgroups)
        const allTargetGroupIds = [...targetGroupIds, ...targetSubgroupIds]

        if (allTargetGroupIds.length === 0) {
          setOverallRate(0)
          setMonthStats({
            confirmed: 0,
            attended: 0,
            missed: 0,
            declined: 0,
            rate: 0,
            total: 0
          })
          setTeamStats({
            memberCount: 0,
            totalEvents: 0,
            confirmationRate: 0,
            attendedRate: 0,
            missedRate: 0,
            declinedRate: 0
          })
          setEvents([])
          return
        }

        // Fetch members from filtered groups/subgroups
        const { data: filteredMembers } = await supabase
          .from('group_members')
          .select('user_id')
          .in('group_id', allTargetGroupIds)

        let memberIds = filteredMembers?.map(m => m.user_id) || []
        
        const memberCount = memberIds.length

        // ===== CALCULATE OVERALL RATE FOR ENTIRE TEAM (unfiltered) =====
        const allGroupIds = groupsData.map(g => g.id)
        const allSubgroupIds = subgroupsData.map(sg => sg.id)
        const allTeamGroupIds = [...allGroupIds, ...allSubgroupIds, teamId]
        
        const { data: allTeamMembers } = await supabase
          .from('group_members')
          .select('user_id')
          .in('group_id', allTeamGroupIds)

        let allTeamMemberIds = allTeamMembers?.map(m => m.user_id) || []
        
        // ALWAYS include Team Leader in overall rate
        if (!allTeamMemberIds.includes(user.id)) {
          allTeamMemberIds.push(user.id)
        }
        
        const { data: allTeamAttendance } = await supabase
          .from('attendance_records')
          .select('*')
          .in('user_id', allTeamMemberIds)

        const teamTotalEvents = allEvents?.length || 0
        const teamPossibleAttendances = teamTotalEvents * allTeamMemberIds.length
        const teamTotalAttendances = allTeamAttendance?.length || 0
        const teamOverallRate = teamPossibleAttendances > 0 
          ? Math.round((teamTotalAttendances / teamPossibleAttendances) * 100) 
          : 0

        setOverallRate(teamOverallRate)

        // Early exit if no members in filtered selection
        if (memberCount === 0) {
          setMonthStats({
            confirmed: 0,
            attended: 0,
            missed: 0,
            declined: 0,
            rate: 0,
            total: monthEvents?.length || 0
          })
          setTeamStats({
            memberCount: 0,
            totalEvents: 0,
            confirmationRate: 0,
            attendedRate: 0,
            missedRate: 0,
            declinedRate: 0
          })
          
          // Still show events with 0 attendance
          const eventsWithZeroAttendance = (monthEvents || []).map(event => ({
            ...event,
            attended: 0,
            confirmed: 0,
            declined: 0,
            status: 'aggregate'
          }))
          
          setEvents(eventsWithZeroAttendance)
        } else {
          // Has members - proceed with normal calculations
          // Fetch confirmations and attendance for FILTERED members
          const { data: allConfirmations } = await supabase
            .from('event_confirmations')
            .select('*')
            .in('user_id', memberIds)

          const { data: allAttendance } = await supabase
            .from('attendance_records')
            .select('*')
            .in('user_id', memberIds)

          // Process month events with aggregate stats
          let confirmedCount = 0
          let attendedCount = 0
          let missedCount = 0
          let declinedCount = 0

          const eventsWithStatus = (monthEvents || []).map((event) => {
            const eventEnd = new Date(`${event.start_date}T${event.end_time}`)
            const isPast = eventEnd < now

            // Count confirmations for this event
            const eventConfirmations = allConfirmations?.filter(c => c.event_id === event.id) || []
            const confirmedForEvent = eventConfirmations.filter(c => c.status === 'confirmed').length
            const declinedForEvent = eventConfirmations.filter(c => c.status === 'declined').length

            // Count attendance for this event
            const eventAttendance = allAttendance?.filter(a => a.event_id === event.id) || []
            const attendedForEvent = eventAttendance.length

            // For past events, calculate missed
            let missedForEvent = 0
            if (isPast) {
              const attendedUserIds = eventAttendance.map(a => a.user_id)
              const declinedUserIds = eventConfirmations
                .filter(c => c.status === 'declined')
                .map(c => c.user_id)
              
              missedForEvent = memberIds.filter(
                mid => !attendedUserIds.includes(mid) && !declinedUserIds.includes(mid)
              ).length
            }

            confirmedCount += confirmedForEvent
            attendedCount += attendedForEvent
            declinedCount += declinedForEvent
            if (isPast) {
              missedCount += missedForEvent
            }

            return {
              ...event,
              attended: attendedForEvent,
              confirmed: confirmedForEvent,
              declined: declinedForEvent,
              missed: isPast ? missedForEvent : 0,
              status: 'aggregate'
            }
          })

          const totalMonthEvents = monthEvents?.length || 0
          const totalPossible = totalMonthEvents * memberCount

          // Calculate rates
          const confirmationRate = totalPossible > 0 ? Math.round((confirmedCount / totalPossible) * 100) : 0
          const attendedRate = totalPossible > 0 ? Math.round((attendedCount / totalPossible) * 100) : 0
          const declinedRate = totalPossible > 0 ? Math.round((declinedCount / totalPossible) * 100) : 0
          
          // Calculate missed rate (only for past events)
          const pastEvents = monthEvents?.filter(e => {
            const eventEnd = new Date(`${e.start_date}T${e.end_time}`)
            return eventEnd < now
          }) || []
          const pastEventsPossible = pastEvents.length * memberCount
          const missedRate = pastEventsPossible > 0 ? Math.round((missedCount / pastEventsPossible) * 100) : 0

          setMonthStats({
            confirmed: confirmedCount,
            attended: attendedCount,
            missed: missedCount,
            declined: declinedCount,
            rate: attendedRate,
            total: totalMonthEvents
          })

          setTeamStats({
            memberCount: memberCount,
            totalEvents: totalMonthEvents,
            confirmationRate: confirmationRate,
            attendedRate: attendedRate,
            missedRate: missedRate,
            declinedRate: declinedRate
          })

          setEvents(eventsWithStatus)
        }
      }

    } catch (error) {
      console.error('Load stats error:', error)
    } finally {
      setLoading(false)
    }
  }

  const getTooltipText = (type) => {
    // Dynamic text based on filter: "team" when all, "group/department" when specific
    const contextText = selectedGroup === 'all' && selectedDepartment === 'all' ? 'team' : 
                        selectedGroup !== 'all' && selectedDepartment === 'all' ? 'group' : 
                        'department'
    
    const tooltips = {
      totalMembers: `Total number of members in your ${contextText}`,
      totalEvents: `Total events in ${months[selectedMonth]} ${selectedYear}`,
      confirmationRate: 'Percentage of members who confirmed attendance across all events',
      attendedRate: 'Percentage of members who actually attended events',
      missedRate: 'Percentage of members who did not attend or respond',
      declinedRate: 'Percentage of members who declined to attend'
    }
    return tooltips[type] || ''
  }

  const handleEventClick = async (event) => {
    // Only work for team tab
    if (activeTab !== 'team') return
    
    setSelectedEvent(event)
    setShowBottomSheet(true)
    setLoadingMembers(true)
    
    try {
      // ===== GET TEAM ID =====
      const { data: { user } } = await supabase.auth.getUser()
      
      const { data: teamMembership } = await supabase
        .from('group_members')
        .select('group_id, groups:group_id(id, name, depth)')
        .eq('user_id', user.id)
        .eq('groups.depth', 1)
        .maybeSingle()
      
      const teamId = teamMembership?.groups?.id
      
      if (!teamId) {
        setLoadingMembers(false)
        return
      }
      
      // ===== APPLY SAME FILTERS AS STATS WITH MEMBERSHIP RULES =====
      let targetGroupIds = []
      let targetSubgroupIds = []

      // Filter by Group
      let filteredGroups = groups
      if (selectedGroup !== 'all') {
        filteredGroups = groups.filter(g => g.id === selectedGroup)
      }

      const filteredGroupIds = filteredGroups.map(g => g.id)
      
      // Filter by Department
      let filteredSubgroups = subgroups
      if (selectedDepartment !== 'all') {
        // Specific department selected
        filteredSubgroups = subgroups.filter(sg => sg.id === selectedDepartment)
      } else if (selectedGroup !== 'all') {
        // Group selected, Department = All → show only subgroups under that group
        filteredSubgroups = subgroups.filter(sg => filteredGroupIds.includes(sg.parent_id))
      }

      // MEMBERSHIP RULE: Only include Group Leaders (depth 2) when Department = "All"
      if (selectedDepartment === 'all') {
        targetGroupIds = filteredGroupIds  // Include Group Leaders
      } else {
        targetGroupIds = []  // Exclude Group Leaders when specific dept selected
      }

      targetSubgroupIds = filteredSubgroups.map(sg => sg.id)

      // Combine depth 2 (groups - only if dept=all) and depth 3 (subgroups)
      const allTargetGroupIds = [...targetGroupIds, ...targetSubgroupIds]
      
      if (allTargetGroupIds.length === 0) {
        setEventMemberDetails([])
        setLoadingMembers(false)
        return
      }
      
      // ===== FETCH MEMBERS FROM FILTERED GROUPS/DEPARTMENTS =====
      const { data: filteredMembers } = await supabase
        .from('group_members')
        .select('user_id')
        .in('group_id', allTargetGroupIds)
      
      let memberIds = filteredMembers?.map(m => m.user_id) || []
      
      if (memberIds.length === 0) {
        setEventMemberDetails([])
        setLoadingMembers(false)
        return
      }
      
      // ===== FETCH USER DETAILS =====
      const { data: users } = await supabase
        .from('users')
        .select('id, name')
        .in('id', memberIds)
      
      // ===== FETCH EVENT DATA =====
      // Get attendance records for this event
      const { data: attendanceRecords } = await supabase
        .from('attendance_records')
        .select('user_id')
        .eq('event_id', event.id)
      
      // Get confirmations for this event
      const { data: confirmations } = await supabase
        .from('event_confirmations')
        .select('user_id, status')
        .eq('event_id', event.id)
      
      // ===== DETERMINE EVENT STATUS =====
      const now = new Date()
      const eventEnd = new Date(`${event.start_date}T${event.end_time}`)
      const isPast = eventEnd < now
      
      // ===== BUILD MEMBER DETAILS WITH STATUS =====
      const memberDetails = memberIds.map(memberId => {
        const userRecord = users?.find(u => u.id === memberId)
        const hasAttended = attendanceRecords?.some(a => a.user_id === memberId)
        const confirmation = confirmations?.find(c => c.user_id === memberId)
        
        // Determine status based on attendance and confirmations
        let status = 'no_response'
        
        if (hasAttended) {
          status = 'attended'
        } else if (confirmation?.status === 'declined') {
          status = 'declined'
        } else if (isPast) {
          status = 'missed'
        } else if (confirmation?.status === 'confirmed') {
          status = 'confirmed'
        }
        
        return {
          id: memberId,
          name: userRecord?.name || 'Unknown',
          status
        }
      })
      
      setEventMemberDetails(memberDetails)
    } catch (error) {
      console.error('Error loading member details:', error)
    } finally {
      setLoadingMembers(false)
    }
  }

  const closeBottomSheet = () => {
    setIsClosing(true)
    setTimeout(() => {
      setShowBottomSheet(false)
      setIsClosing(false)
    }, 300) // Match animation duration
  }

  const getStatusBadge = (status) => {
    if (status === 'attended') {
      return (
        <span className="bg-[#eaf5ed] text-[#2b9646] px-3 py-1 rounded-full text-xs font-normal">
          Attended
        </span>
      )
    }
    if (status === 'missed') {
      return (
        <span className="bg-[#fff4ed] text-[#d97706] px-3 py-1 rounded-full text-xs font-normal">
          Missed
        </span>
      )
    }
    if (status === 'declined') {
      return (
        <span className="bg-[#fff4ed] text-[#d97706] px-3 py-1 rounded-full text-xs font-normal">
          Declined
        </span>
      )
    }
    if (status === 'confirmed') {
      return (
        <span className="bg-[#eaf5ed] text-[#2b9646] px-3 py-1 rounded-full text-xs font-normal">
          Confirmed
        </span>
      )
    }
    return (
      <span className="bg-slate-50 text-slate-500 px-3 py-1 rounded-full text-xs font-normal">
        No response
      </span>
    )
  }

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]

  if (loading) {
    return <LoadingSpinner message="Loading statistics..." />
  }

  return (
    <>
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes fadeOut {
          from {
            opacity: 1;
          }
          to {
            opacity: 0;
          }
        }

        @keyframes slideUp {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }

        @keyframes slideDown {
          from {
            transform: translateY(0);
          }
          to {
            transform: translateY(100%);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out both;
        }

        .animate-fadeOut {
          animation: fadeOut 0.3s ease-out both;
        }

        .animate-slideUp {
          animation: slideUp 0.3s ease-out both;
        }

        .animate-slideDown {
          animation: slideDown 0.3s ease-out both;
        }
      `}</style>
      <PullToRefresh onRefresh={loadStats}>
        <div className="bg-white min-h-screen pb-20">
          {/* Header */}
          <div className="bg-white px-6 pt-10 pb-4">
            <div className="flex justify-between items-center">
              <h1 className="text-2xl font-semibold text-[#1E293B]">
                {activeTab === 'personal' ? 'Stats' : 'Stats'}
              </h1>
              <button className="w-10 h-10 flex items-center justify-center bg-slate-50 border border-slate-200 rounded-[20px]">
                <Bell size={20} weight="fill" className="text-[#64748B]" />
              </button>
            </div>
          </div>

          {/* Segmented Control */}
          <div className="px-6 pb-4">
            <div className="bg-slate-50 border border-slate-200 rounded-[15px] p-1 flex gap-1">
              <button
                onClick={() => setActiveTab('personal')}
                className={`flex-1 px-4 py-2.5 rounded-[12px] text-sm font-regular transition-all ${
                  activeTab === 'personal'
                    ? 'bg-[#D6e1ff] text-[#1E293B] shadow-sm'
                    : 'text-slate-600 hover:text-slate-800'
                }`}
              >
                My Stats
              </button>
              <button
                onClick={() => setActiveTab('team')}
                className={`flex-1 px-4 py-2.5 rounded-[12px] text-sm font-regular transition-all ${
                  activeTab === 'team'
                    ? 'bg-[#D6e1ff] text-[#1E293B] shadow-sm'
                    : 'text-slate-600 hover:text-slate-800'
                }`}
              >
                My Team's
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="px-6 pb-6">
            <div className="flex gap-3">
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="flex-1 px-4 py-3 bg-white border border-slate-200 rounded-[15px] text-base text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#3167fe]"
              >
                {months.map((month, index) => (
                  <option key={index} value={index}>
                    {month}
                  </option>
                ))}
              </select>

              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="flex-1 px-4 py-3 bg-white border border-slate-200 rounded-[15px] text-base text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#3167fe]"
              >
                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 space-y-4">
            {/* Overall Attendance Rate Card */}
            <div 
              className="rounded-[25px] p-6 text-white relative overflow-hidden"
              style={{
                backgroundColor: activeTab === 'personal' ? '#2248B2' : '#DA6230'
              }}
            >
              {/* Pattern overlay */}
              <div 
                className="absolute inset-0 opacity-40"
                style={{
                  backgroundImage: activeTab === 'personal' ? 'url(/Line.png)' : 'url(/Line2.png)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              ></div>

              {/* Content - needs to be above pattern */}
              <div className="relative z-10">
                <div className="absolute top-0 left-0">
                  <div className="">
                    <CalendarDots size={40} weight="fill" className={activeTab === 'personal' ? 'text-[#ADC2FF]' : 'text-[#fbd3c2]'} />
                  </div>
                </div>
                
                <div className="pt-20 pb-4 text-left">
                  <div className={`text-7xl font-bold mb-3 leading-none ${activeTab === 'personal' ? 'text-[#D6E1FF]' : 'text-[#fbd3c2]'}`}>
                    {overallRate}%
                  </div>
                  <p className={`text-xl ${activeTab === 'personal' ? 'text-[#adc2ff]' : 'text-[#fbd3c2]'}`}>
                    {activeTab === 'personal' ? (
                      <>My overall<br/>attendance rate</>
                    ) : teamData?.name ? (
                      <>{teamData.name}'s overall<br/>attendance rate</>
                    ) : (
                      <>Team's overall<br/>attendance rate</>
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Group and Department Filters - Only show in team tab */}
            {activeTab === 'team' && (
              <div className="mt-6">
                {/* Single Filter Header */}
                <label className="block text-sm font-medium text-slate-700 mb-2">Filter</label>
                
                {/* Horizontal Filter Layout */}
                <div className="flex gap-3">
                  {/* Group Filter */}
                  <div className="flex-1 relative">
                    <select
                      value={selectedGroup}
                      onChange={(e) => setSelectedGroup(e.target.value)}
                      className="appearance-none w-full bg-white border border-slate-300 rounded-lg px-3 py-2 pr-8 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="all">All sub teams</option>
                      {groups.map(g => (
                        <option key={g.id} value={g.id}>{g.name}</option>
                      ))}
                    </select>
                    <CaretDown size={16} weight="regular" className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-600" />
                  </div>

                  {/* Department Filter */}
                  <div className="flex-1 relative">
                    <select
                      value={selectedDepartment}
                      onChange={(e) => setSelectedDepartment(e.target.value)}
                      className="appearance-none w-full bg-white border border-slate-300 rounded-lg px-3 py-2 pr-8 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="all">All departments</option>
                      {subgroups.map(sg => (
                        <option key={sg.id} value={sg.id}>{sg.name}</option>
                      ))}
                    </select>
                    <CaretDown size={16} weight="regular" className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-600" />
                  </div>
                </div>
              </div>
            )}

            {/* Month Stats Card */}
            <div className="bg-white rounded-[20px] border border-slate-200 p-5">
              <h2 className="text-xl  text-[#475569] font-semibold text-slate-800 mb-4">
                Stats - {months[selectedMonth]}, {selectedYear}
              </h2>
              
              {/* Attendance rate */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-base text-[#1E293B] font-light ">Attendance rate</span>
                  <span className="text-base text-[#1E293B] font-bold ">{monthStats.rate}%</span>
                </div>
                
                <div className="w-full bg-[#EAF0FF] rounded-full h-6 mb-2">
                  <div 
                    className="bg-[#3167FE] h-6 rounded-full transition-all duration-500"
                    style={{ width: `${monthStats.rate}%` }}
                  ></div>
                </div>
                
                <p className="text-sm text-[#475569] mb-10" >
                  <span className="font-semibold text-[#1E293B]">{monthStats.attended} of {monthStats.total}</span> Total event(s)
                </p>
              </div>

              {/* Stats list - Different for personal vs department */}
              {activeTab === 'personal' ? (
                <div className="space-y-3 mt-4">
                  <div className="flex items-center justify-between gap-3 py-3 border-b border-slate-100 last:border-0">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-[#3167fe]"></div>
                      <span className="text-sm text-[#475569]">Events Confirmed</span>
                    </div>
                    <span className="font-semibold text-[#1E293B]">{monthStats.confirmed}</span>
                  </div>

                  <div className="flex items-center justify-between gap-3 py-3 border-b border-slate-100 last:border-0">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-[#2b9646]"></div>
                      <span className="text-sm text-[#475569]">Events Attended</span>
                    </div>
                    <span className="font-semibold text-[#1E293B]">{monthStats.attended}</span>
                  </div>

                  <div className="flex items-center justify-between gap-3 py-3 border-b border-slate-100 last:border-0">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-[#F26D35]"></div>
                      <span className="text-sm text-[#475569]">Events Missed</span>
                    </div>
                    <span className="font-semibold text-[#1E293B]">{monthStats.missed}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-[#D00E0E]"></div>
                      <span className="text-sm text-[#475569]">Events Declined</span>
                    </div>
                    <span className="font-semibold text-[#1E293B]">{monthStats.declined}</span>
                  </div>
                </div>
              ) : (
                <div className="space-y-3 mt-4">
                  {/* Total Members */}
                  <div className="flex items-center justify-between gap-3 py-3 border-b border-slate-100">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-[#3167fe]"></div>
                      <span className="text-sm text-[#475569]">Total Members</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setActiveTooltip(activeTooltip === 'totalMembers' ? null : 'totalMembers')
                        }}
                        className="text-slate-400 hover:text-slate-600 transition-colors relative"
                      >
                        <Info size={16} weight="fill" />
                        {activeTooltip === 'totalMembers' && (
                          <div className="absolute left-0 top-6 bg-slate-800 text-white text-xs rounded-lg py-2 px-3 w-48 z-50 shadow-lg">
                            {getTooltipText('totalMembers')}
                            <div className="absolute -top-1 left-4 w-2 h-2 bg-slate-800 transform rotate-45"></div>
                          </div>
                        )}
                      </button>
                    </div>
                    <span className="font-semibold text-[#1E293B]">{teamStats.memberCount}</span>
                  </div>

                  {/* Total Events */}
                  <div className="flex items-center justify-between gap-3 py-3 border-b border-slate-100">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-[#3167fe]"></div>
                      <span className="text-sm text-[#475569]">Total events</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); setActiveTooltip(activeTooltip === 'totalEvents' ? null : 'totalEvents') }}
                        className="text-slate-400 hover:text-slate-600 transition-colors relative"
                      >
                        <Info size={16} weight="fill" />
                        {activeTooltip === 'totalEvents' && (
                          <div className="absolute left-0 top-6 bg-slate-800 text-white text-xs rounded-lg py-2 px-3 w-48 z-50 shadow-lg">
                            {getTooltipText('totalEvents')}
                            <div className="absolute -top-1 left-4 w-2 h-2 bg-slate-800 transform rotate-45"></div>
                          </div>
                        )}
                      </button>
                    </div>
                    <span className="font-semibold text-[#1E293B]">{teamStats.totalEvents}</span>
                  </div>

                  {/* Confirmation Rate */}
                  <div className="flex items-center justify-between gap-3 py-3 border-b border-slate-100">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-[#3167fe]"></div>
                      <span className="text-sm text-[#475569]">Confirmation rate</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); setActiveTooltip(activeTooltip === 'confirmationRate' ? null : 'confirmationRate') }}
                        className="text-slate-400 hover:text-slate-600 transition-colors relative"
                      >
                        <Info size={16} weight="fill" />
                        {activeTooltip === 'confirmationRate' && (
                          <div className="absolute left-0 top-6 bg-slate-800 text-white text-xs rounded-lg py-2 px-3 w-48 z-50 shadow-lg">
                            {getTooltipText('confirmationRate')}
                            <div className="absolute -top-1 left-4 w-2 h-2 bg-slate-800 transform rotate-45"></div>
                          </div>
                        )}
                      </button>
                    </div>
                    <span className="font-semibold text-[#1E293B]">{teamStats.confirmationRate}%</span>
                  </div>

                  {/* Attended Rate */}
                  <div className="flex items-center justify-between gap-3 py-3 border-b border-slate-100">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-[#2b9646]"></div>
                      <span className="text-sm text-[#475569]">Attended rate</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); setActiveTooltip(activeTooltip === 'attendedRate' ? null : 'attendedRate') }}
                        className="text-slate-400 hover:text-slate-600 transition-colors relative"
                      >
                        <Info size={16} weight="fill" />
                        {activeTooltip === 'attendedRate' && (
                          <div className="absolute left-0 top-6 bg-slate-800 text-white text-xs rounded-lg py-2 px-3 w-48 z-50 shadow-lg">
                            {getTooltipText('attendedRate')}
                            <div className="absolute -top-1 left-4 w-2 h-2 bg-slate-800 transform rotate-45"></div>
                          </div>
                        )}
                      </button>
                    </div>
                    <span className="font-semibold text-[#1E293B]">{teamStats.attendedRate}%</span>
                  </div>

                  {/* Missed Rate */}
                  <div className="flex items-center justify-between gap-3 py-3 border-b border-slate-100">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-[#F26D35]"></div>
                      <span className="text-sm text-[#475569]">Missed rate</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); setActiveTooltip(activeTooltip === 'missedRate' ? null : 'missedRate') }}
                        className="text-slate-400 hover:text-slate-600 transition-colors relative"
                      >
                        <Info size={16} weight="fill" />
                        {activeTooltip === 'missedRate' && (
                          <div className="absolute left-0 top-6 bg-slate-800 text-white text-xs rounded-lg py-2 px-3 w-48 z-50 shadow-lg">
                            {getTooltipText('missedRate')}
                            <div className="absolute -top-1 left-4 w-2 h-2 bg-slate-800 transform rotate-45"></div>
                          </div>
                        )}
                      </button>
                    </div>
                    <span className="font-semibold text-[#1E293B]">{teamStats.missedRate}%</span>
                  </div>

                  {/* Declined Rate */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-[#D00E0E]"></div>
                      <span className="text-sm text-[#475569]">Declined rate</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); setActiveTooltip(activeTooltip === 'declinedRate' ? null : 'declinedRate') }}
                        className="text-slate-400 hover:text-slate-600 transition-colors relative"
                      >
                        <Info size={16} weight="fill" />
                        {activeTooltip === 'declinedRate' && (
                          <div className="absolute left-0 top-6 bg-slate-800 text-white text-xs rounded-lg py-2 px-3 w-48 z-50 shadow-lg">
                            {getTooltipText('declinedRate')}
                            <div className="absolute -top-1 left-4 w-2 h-2 bg-slate-800 transform rotate-45"></div>
                          </div>
                        )}
                      </button>
                    </div>
                    <span className="font-semibold text-[#1E293B]">{teamStats.declinedRate}%</span>
                  </div>
                </div>
              )}
            </div>

            {/* Events List Card */}
            <div className="bg-white rounded-[20px] border border-slate-200 p-5">
              <h2 className="text-xl font-semibold text-[#475569] mb-4">
                Events - {months[selectedMonth]}, {selectedYear}
              </h2>
              
              {events.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-6">
                  No events in {months[selectedMonth]} {selectedYear}
                </p>
              ) : (
                <div className="space-y-3">
                  {events.map((event) => (
                    <div 
                      key={event.id}
                      onClick={() => activeTab === 'team' && handleEventClick(event)}
                      className={`flex items-center gap-3 py-3 border-b border-slate-100 last:border-0 ${
                        activeTab === 'team' ? 'cursor-pointer hover:bg-slate-50 transition-colors rounded-lg px-2 -mx-2' : ''
                      }`}
                    >
                      <div className="bg-slate-100 rounded-[12px] p-2 flex-shrink-0">
                        <CalendarBlank size={24} weight="fill" className="text-slate-600" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{event.title}</p>
                        <p className="text-xs text-slate-500">
                          {new Date(event.start_date).toLocaleDateString('en-US', { 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </p>
                      </div>
                      
                      {activeTab === 'personal' ? (
                        getStatusBadge(event.status)
                      ) : (
                        <div className="text-right">
                          <p className="text-sm font-semibold text-slate-800">
                            {event.attended || 0} attended
                          </p>
                          <p className="text-xs text-slate-500">
                            out of {teamStats.memberCount} member(s)
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </PullToRefresh>
      
      {/* Bottom Sheet for Event Attendance Details */}
      {showBottomSheet && (
        <>
          {/* Backdrop with conditional fade animation - stops above bottom nav */}
          <div 
            className={`fixed inset-0 bottom-20 bg-black/50 z-[60] ${isClosing ? 'animate-fadeOut' : 'animate-fadeIn'}`}
            onClick={closeBottomSheet}
          />
          
          {/* Bottom Sheet with smooth slide animation */}
          <div 
            className={`fixed inset-x-0 bottom-0 z-[70] bg-white rounded-t-[24px] shadow-xl max-h-[70vh] overflow-hidden 
            ${!isClosing ? 'animate-slideUp' : 'animate-slideDown'}`}
            style={!isClosing ? { transform: 'translateY(100%)' } : undefined}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-800">Event attendance</h3>
              <button 
                onClick={closeBottomSheet}
                className="p-2 hover:bg-slate-100 rounded-full transition-colors"
              >
                <X size={24} className="text-slate-600" />
              </button>
            </div>
            
            {/* Content */}
            <div className="overflow-y-auto max-h-[calc(70vh-80px)] pb-24">
              {loadingMembers ? (
                <div className="flex items-center justify-center py-12">
                  <LoadingSpinner />
                </div>
              ) : (
                <>
                  {/* Summary */}
                  <div className="px-5 py-4 bg-slate-50 border-b border-slate-200">
                    <p className="text-sm text-slate-600">
                      <span className="font-semibold text-slate-800">
                        {eventMemberDetails.filter(m => m.status === 'attended').length}
                      </span>
                      {' '}of{' '}
                      <span className="font-semibold text-slate-800">
                        {eventMemberDetails.length}
                      </span>
                      {' '}members attended
                    </p>
                    {selectedEvent && (
                      <p className="text-xs text-slate-500 mt-1">
                        {selectedEvent.title} • {new Date(selectedEvent.start_date).toLocaleDateString('en-US', { 
                          month: 'long', 
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </p>
                    )}
                  </div>
                  
                  {/* Member List */}
                  <div className="p-5 space-y-3">
                    {eventMemberDetails.map((member) => (
                      <div 
                        key={member.id}
                        className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">
                            {member.name}
                          </p>
                        </div>
                        
                        <div className="ml-3">
                          {getStatusBadge(member.status)}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}
      
      <TeamLeaderBottomNav />
    </>
  )
}